import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ConfigService } from '../../../services/config.service';

@Injectable()
export class PurchasesService {
    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private translateService: TranslateService,
    ) {}

    getSubscriptions(userId?: string): Observable<Subscription[]> {
        userId = userId || this.configService.me.id;

        return this.http.get<Subscription[]>(`${environment.http.purchasesBaseUrl}users/${userId}/subscriptions`).pipe(
            map((subscriptions) => {
                return subscriptions.map((subscription) => {
                    return {
                        ...subscription,
                        name: this.getSubNameTranslationKey(subscription.name),
                        billingTranslationKey: this.getBillingTranslationKey(subscription.billing),
                        isTrial: this.isTrial(subscription),
                        status:
                            this.isTrial(subscription) && subscription.status === SubscriptionStatus.Active
                                ? SubscriptionStatus.Trial
                                : subscription.status,
                    };
                });
            }),
        );
    }

    getSubscriptionDetails(subscriptionId: string): Observable<SubscriptionDetails> {
        return this.http
            .get<SubscriptionDetails>(`${environment.http.purchasesBaseUrl}subscriptions/${subscriptionId}`)
            .pipe(
                map((subscription) => {
                    return {
                        ...subscription,
                        name: this.getSubNameTranslationKey(subscription.name),
                        isTrial: this.isTrial(subscription),
                        billingTranslationKey: this.getBillingTranslationKey(subscription.billing),
                        features: subscription?.features?.map((feature) => {
                            return {
                                ...feature,
                                name: feature.name === 'NumberOfAPICalls' ? 'Number of API calls' : feature.name,
                                description: this.getFeatureDescription(feature.name),
                            };
                        }),
                    };
                }),
            );
    }

    private getFeatureDescription(name: string) {
        const key = `SELF_SERVICE.PURCHASES_AN_SUBSCRIPTIONS.FEATURE_DETAILS.DESCRIPTION.${name
            .toUpperCase()
            .replace(' ', '_')}`;
        const translation = this.translateService.instant(key);
        return translation === key ? '' : <string>translation;
    }

    getDocumentFromArchive(documentUri: string, objectKey: string): Observable<ArchiveDocument> {
        return this.http.get<ArchiveDocument>(
            `${environment.http.purchasesBaseUrl}documents/${documentUri}/${objectKey}`,
        );
    }

    cancelSubscription(subscriptionId: string, cancellationDetails: CancellationDetails): Observable<void> {
        return this.http.put<void>(
            `${environment.http.purchasesBaseUrl}subscriptions/${subscriptionId}`,
            cancellationDetails,
        );
    }

    updateCreditCard(subscriptionId: string): Observable<PaymentInformation> {
        return this.http.post<PaymentInformation>(
            `${environment.http.purchasesBaseUrl}subscriptions/${subscriptionId}/update-credit-card`,
            {
                redirectUrlTemplate: `${environment.baseUrl}/purchases/${subscriptionId}?status=<status>&payment-request-id=<paymentRequestId>`,
            },
        );
    }

    updateSubscription(subscriptionId: string, transactionId: string, transactionStatus: string): Observable<void> {
        return this.http.patch<void>(`${environment.http.purchasesBaseUrl}subscriptions/${subscriptionId}`, {
            transactionId: transactionId,
            transactionStatus: transactionStatus,
        });
    }

    private getBillingTranslationKey(billing: string) {
        switch (billing) {
            case 'monthly':
                return 'SELF_SERVICE.PURCHASES_AN_SUBSCRIPTIONS.OVERVIEW.SUBSCRIPTION_CARD.BILLIG_MONTHLY';
            default:
                return billing;
        }
    }

    private getSubNameTranslationKey(subscriptionName: string) {
        switch (subscriptionName) {
            case 'custom-plan':
                return this.translateService.instant(
                    'SELF_SERVICE.PURCHASES_AN_SUBSCRIPTIONS.OVERVIEW.SUBSCRIPTION_CARD.CUSTOM_SUBSCRIPTION_TITLE',
                );
            default:
                return subscriptionName;
        }
    }

    /* If either the payment method is "trial" or the subscription start date is in the future.
     * The specified subscription is a trial or an automatically created free trial. */
    private isTrial(subscription: Subscription): boolean {
        return (
            subscription.paymentMethod === 'trial' ||
            dayjs(subscription.subscriptionDate, 'YYYY-MM-DD').isAfter(dayjs())
        );
    }

    revokeCancellation(subscriptionId: string) {
        return this.http.put(`${environment.http.purchasesBaseUrl}subscriptions/${subscriptionId}/revocation`, {});
    }
}

export interface Subscription {
    id: string;
    name: string;
    materialNumber: string;
    billing: string;
    billingTranslationKey?: string;
    status: SubscriptionStatus;
    subscriptionDate: string;
    expirationDate: string;
    cancellationDate: string;
    isTrial?: boolean;
    paymentMethod?: string;
    cancellationDueDate?: string;
    sapContractId?: string;
}

export interface SubscriptionDetails extends Subscription {
    currency: string;
    price: string;
    invoices: Invoice[];
    features: SubscriptionFeature[];
    materialNumbers: string[];
    billing: 'monthly' | 'yearly';
}

export interface SubscriptionFeature {
    name: string;
    description?: string;
    quantity?: number;
}

export interface Invoice {
    id: string;
    filename: string;
    documentUri: string;
    creationTime: string;
}

export enum SubscriptionStatus {
    Active = 'active',
    Canceled = 'canceled',
    Expired = 'expired',
    Trial = 'trial',
}

export interface ArchiveDocument {
    filename: string;
    mimetype: string;
    data: string;
}

export interface PaymentInformation {
    paymentRequestId: string;
    stripePublicApiKey: string;
    stripeSessionId: string;
    stripeRedirectUrl: string;
}

export interface CancellationDetails {
    cancelationReason: string;
    additionalComment?: string;
}
